import unauthAxios, { unauthAxiosFetcher } from "../axios";
import authAxios, { authAxiosFetcher } from "../authAxios";
import useSWR from "swr";

export const useEvents = () => useSWR("v1/soclub/getAllEvents", unauthAxiosFetcher);

export const useFollowingTrips = (userId) => {
  return useSWR(
    userId ? `/v1/social/getFollowingTrips?userId=${userId}` : null,
    unauthAxiosFetcher
  );
};

export const getEventById = (id) => unauthAxios.get(`v1/soclub/getEventById?activityId=${id}`);

// ---------------------------------------------------------------------------
// Sotravel Club platform (/v1/club/*): self-serve clubs that run local events.
// Distinct from the legacy Soclub endpoints above.
// ---------------------------------------------------------------------------

const clubPath = (clubId, suffix = "") => `/v1/club/${encodeURIComponent(clubId)}${suffix}`;

export const checkClubHandle = (handle) =>
  authAxios.get("/v1/club/handle-available", { params: { handle } }).then((r) => r.data);

export const suggestClubHandle = (name) =>
  authAxios.get("/v1/club/suggest-handle", { params: { name } }).then((r) => r.data);

export const createClubAccount = (payload) => authAxios.post("/v1/club", payload).then((r) => r.data);

export const getMyClubs = () => authAxios.get("/v1/club/mine").then((r) => r.data.clubs || []);

export const useMyClubs = (enabled = true) =>
  useSWR(enabled ? "/v1/club/mine" : null, authAxiosFetcher, { revalidateOnFocus: false });

// Scope the cache to the signed-in user so permissions cannot survive account changes.
export const useCanManageClubEvent = (userId, clubId, projectId) => {
  const { data } = useSWR(
    userId && clubId && projectId ? ["club-event-management", userId, clubId, projectId] : null,
    async () => {
      const clubs = await getMyClubs();
      const club = clubs.find((item) => String(item.id) === String(clubId));
      if (["admin", "finance_admin", "sotravel_admin"].includes(club?.role)) return true;
      if (club?.role !== "event_host") return false;
      const events = await listClubEvents(clubId);
      return events.some((event) => String(event.id) === String(projectId));
    }
  );
  return Boolean(userId && clubId && projectId && data);
};

export const getClubDetail = (clubId) => authAxios.get(clubPath(clubId)).then((r) => r.data);

// Change the club's public address (/club/<handle>). 400/409 with a readable
// message when the handle is invalid, reserved or taken.
export const updateClubHandle = (clubId, handle) =>
  authAxios.put(clubPath(clubId, "/handle"), { handle }).then((r) => r.data);
export const updateClubProfile = (clubId, payload) =>
  authAxios.put(clubPath(clubId), payload).then((r) => r.data);

export const listClubMembers = (clubId) =>
  authAxios.get(clubPath(clubId, "/members")).then((r) => r.data.members || []);

export const addClubMember = (clubId, payload) =>
  authAxios.post(clubPath(clubId, "/members"), payload).then((r) => r.data.members || []);

export const updateClubMemberRole = (clubId, memberId, role) =>
  authAxios.put(clubPath(clubId, `/members/${memberId}`), { role }).then((r) => r.data.members || []);

export const removeClubMember = (clubId, memberId) =>
  authAxios.delete(clubPath(clubId, `/members/${memberId}`)).then((r) => r.data.members || []);

export const listClubEvents = (clubId) =>
  authAxios.get(clubPath(clubId, "/events")).then((r) => r.data.events || []);

export const listClubEventTemplates = () =>
  authAxios.get("/v1/club/event-templates").then((r) => r.data.events || []);

export const createClubEvent = (clubId, payload) =>
  authAxios.post(clubPath(clubId, "/events"), payload).then((r) => r.data);

export const getClubEvent = (clubId, projectId) =>
  authAxios.get(clubPath(clubId, `/events/${projectId}`)).then((r) => r.data);

export const searchClubEventHostAccounts = (clubId, projectId, q) =>
  authAxios.get(clubPath(clubId, `/events/${projectId}/hosts/search`), { params: { q } }).then((r) => r.data.accounts || []);

export const addClubEventHost = (clubId, projectId, userId) =>
  authAxios.post(clubPath(clubId, `/events/${projectId}/hosts`), { user_id: userId }).then((r) => r.data.hosts || []);

export const inviteClubEventHost = (clubId, projectId, email) =>
  authAxios.post(clubPath(clubId, `/events/${projectId}/hosts/invite`), { email }).then((r) => r.data.hosts || []);

export const removeClubEventHost = (clubId, projectId, userId) =>
  authAxios.delete(clubPath(clubId, `/events/${projectId}/hosts/${userId}`)).then((r) => r.data.hosts || []);

export const getClubEventTemplate = (projectId) =>
  authAxios.get(`/v1/club/event-templates/${projectId}`).then((r) => r.data);

export const getClubEventByTrip = (clubId, tripId) =>
  authAxios.get(clubPath(clubId, `/events/by-trip/${tripId}`)).then((r) => r.data);

export const getClubEventNotifications = (clubId, tripId, { summary = false } = {}) =>
  authAxios
    .get(clubPath(clubId, `/events/by-trip/${tripId}/notifications`), { params: summary ? { summary: 1 } : {} })
    .then((r) => r.data);

export const postClubEventNotification = (clubId, tripId, payload) =>
  authAxios.post(clubPath(clubId, `/events/by-trip/${tripId}/notifications`), payload).then((r) => r.data);

export const markClubEventNotificationsRead = (clubId, tripId, lastReadAt) =>
  authAxios
    .post(clubPath(clubId, `/events/by-trip/${tripId}/notifications/read`), { last_read_at: lastReadAt })
    .then((r) => r.data);

export const updateClubEvent = (clubId, projectId, payload) =>
  authAxios.put(clubPath(clubId, `/events/${projectId}`), payload).then((r) => r.data);

export const confirmClubEvent = (clubId, projectId) =>
  authAxios.post(clubPath(clubId, `/events/${projectId}/confirm`)).then((r) => r.data);

export const addClubEventDates = (clubId, projectId, dates) =>
  authAxios.post(clubPath(clubId, `/events/${projectId}/dates`), { dates }).then((r) => r.data);

export const cancelClubEventDate = (clubId, projectId, tripId, reason) =>
  authAxios
    .post(clubPath(clubId, `/events/${projectId}/dates/${tripId}/cancel`), { reason })
    .then((r) => r.data);

export const cancelClubEvent = (clubId, projectId, reason) =>
  authAxios.post(clubPath(clubId, `/events/${projectId}/cancel`), { reason }).then((r) => r.data);

export const cloneClubEvent = (clubId, projectId) =>
  authAxios.post(clubPath(clubId, `/events/${projectId}/clone`)).then((r) => r.data);
export const searchClubTransferTargets = (q, sourceClubId) =>
  authAxios.get("/v1/club/transfer-targets", { params: { q, source_club_id: sourceClubId } }).then((r) => r.data.targets || []);
export const requestClubEventTransfer = (clubId, projectId, target) =>
  authAxios.post(clubPath(clubId, `/events/${projectId}/transfer`), target).then((r) => r.data);
export const cancelClubEventTransfer = (clubId, projectId) =>
  authAxios.delete(clubPath(clubId, `/events/${projectId}/transfer`)).then((r) => r.data);
export const listIncomingClubEventTransfers = () =>
  authAxios.get("/v1/club/event-transfers/incoming").then((r) => r.data.transfers || []);
export const acceptClubEventTransfer = (transferId) =>
  authAxios.post(`/v1/club/event-transfers/${transferId}/accept`).then((r) => r.data);

export const getClubAnalytics = (clubId, params = {}) =>
  authAxios.get(clubPath(clubId, "/analytics"), { params }).then((r) => r.data);

export const getClubEarnings = (clubId) =>
  authAxios.get(clubPath(clubId, "/earnings")).then((r) => r.data);

export const updateClubPayoutDetails = (clubId, payload) =>
  authAxios.put(clubPath(clubId, "/payout-details"), payload).then((r) => r.data);

export const requestClubWithdrawal = (clubId, payload = {}) =>
  authAxios.post(clubPath(clubId, "/withdrawals"), payload).then((r) => r.data);

// Luma-style additions (2026-09-22)
// Personal club: every person's own hosting account, created on first use.
export const ensurePersonalClub = () => authAxios.post("/v1/club/personal").then((r) => r.data);

// Venue search for the event location picker (Google or OpenStreetMap, server decides).
export const searchPlaces = (q, community, session) =>
  authAxios.get("/v1/club/places/autocomplete", { params: { q, community, session } }).then((r) => r.data);
export const getPlaceDetails = (id, session) =>
  authAxios.get("/v1/club/places/details", { params: { id, session } }).then((r) => r.data);
// Public: Maps Embed key (referrer-restricted) + which venue provider is live.
export const getMapsConfig = () => unauthAxios.get("/v1/club/maps-config").then((r) => r.data);

// Curated cover photos for the "Choose image" sheet.
export const getCoverGallery = () => authAxios.get("/v1/club/cover-gallery").then((r) => r.data.categories || []);

// Guests of an event (all dates) + check-in.
export const listClubEventGuests = (clubId, projectId) =>
  authAxios.get(clubPath(clubId, `/events/${projectId}/guests`)).then((r) => r.data.guests || []);
export const listClubTripGuests = (clubId, tripId) =>
  authAxios.get(clubPath(clubId, `/events/by-trip/${tripId}/guests`)).then((r) => r.data.guests || []);
// Full guests payload: { guests, no_show_window } (the window is null when the
// event has no no-show fee).
export const getClubTripGuestsPayload = (clubId, tripId) =>
  authAxios
    .get(clubPath(clubId, `/events/by-trip/${tripId}/guests`))
    .then((r) => ({
      guests: r.data.guests || [],
      no_show_window: r.data.no_show_window || null,
      questions: r.data.questions || [],
    }));
export const chargeClubNoShowFee = (clubId, projectId, userTripId) =>
  authAxios.post(clubPath(clubId, `/events/${projectId}/guests/${userTripId}/no-show-fee`)).then((r) => r.data);
export const chargeClubNoShowFees = (clubId, projectId, userTripIds) =>
  authAxios
    .post(clubPath(clubId, `/events/${projectId}/no-show-fees`), { user_trip_ids: userTripIds })
    .then((r) => r.data);
export const lookupClubTripGuest = (clubId, tripId, email) =>
  authAxios.post(clubPath(clubId, `/events/by-trip/${tripId}/guest-lookup`), { email }).then((r) => r.data);
export const getClubTripBookingOptions = (clubId, tripId) =>
  authAxios.get(clubPath(clubId, `/events/by-trip/${tripId}/booking-options`)).then((r) => r.data);
export const addClubTripGuest = (clubId, tripId, payload) =>
  authAxios.post(clubPath(clubId, `/events/by-trip/${tripId}/guests/direct`), payload).then((r) => r.data);
export const listClubInviteSources = (clubId, tripId) =>
  authAxios.get(clubPath(clubId, `/events/by-trip/${tripId}/invite-sources`)).then((r) => r.data.events || []);
export const sendClubTripInvites = (clubId, tripId, payload) =>
  authAxios.post(clubPath(clubId, `/events/by-trip/${tripId}/invites`), payload).then((r) => r.data);
export const sendClubWaitlistInvites = (clubId, tripId, payload) =>
  authAxios.post(clubPath(clubId, `/events/by-trip/${tripId}/waitlist-invites`), payload).then((r) => r.data);
export const setClubGuestCheckIn = (clubId, projectId, userTripId, checkedIn) =>
  authAxios
    .post(clubPath(clubId, `/events/${projectId}/guests/${userTripId}/check-in`), { checked_in: checkedIn })
    .then((r) => r.data);
export const decideClubGuestBooking = (clubId, tripId, userTripId, decision) =>
  authAxios
    .post(clubPath(clubId, `/events/by-trip/${tripId}/guests/${userTripId}/decision`), { decision })
    .then((r) => r.data);
export const cancelClubGuestBooking = (clubId, tripId, userTripId) =>
  authAxios.post(clubPath(clubId, `/events/by-trip/${tripId}/guests/${userTripId}/cancel`)).then((r) => r.data);

// Bookings & referrals (sales) + Stripe-style activity feed.
export const getClubSales = (clubId, params = {}) =>
  authAxios.get(clubPath(clubId, "/sales"), { params }).then((r) => r.data);
export const getClubActivity = (clubId, params = {}) =>
  authAxios.get(clubPath(clubId, "/activity"), { params }).then((r) => r.data.activity || []);

// Public event page → "N going" or "N interested" plus a few guest faces for a
// date. The BE decides which (`show`: "going" from 10 going guests, else
// "interested", null when there's nothing to say). The viewer's id rides in
// the UserId header (blocked people never appear), so it is part of the key.
export const useClubEventAttendance = (tripId, userId) =>
  useSWR(tripId ? [`/v1/club/trips/${tripId}/attendance`, userId || null] : null, ([url]) => unauthAxiosFetcher(url), {
    revalidateOnFocus: false,
  });

// Public event page → one view per browser session. Fire-and-forget.
export const trackClubEventView = (projectId, payload = {}) => {
  const key = `club_event_view_${projectId}`;
  try {
    if (sessionStorage.getItem(key)) return Promise.resolve(null);
    sessionStorage.setItem(key, "1");
  } catch (e) {
    /* storage unavailable: still record once */
  }
  return unauthAxios.post(`/v1/club/events/${projectId}/view`, payload).catch(() => null);
};

// A logged-out club-page visit is tracked by browser client id; once the
// visitor logs in, attach those visits to the account so bookings attribute.
export const claimClubVisits = (clientId) =>
  authAxios.post("/v1/club/claim-visits", { client_id: clientId }).then((r) => r.data);

// ---- Overseas trips hosted by a club (/club/manage/trips/*) ----
export const listClubTripCountries = () =>
  authAxios.get("/v1/club/trip-countries").then((r) => r.data.countries || []);
export const createClubTrip = (clubId, payload) =>
  authAxios.post(clubPath(clubId, "/trips"), payload).then((r) => r.data);
export const getClubTrip = (clubId, projectId) =>
  authAxios.get(clubPath(clubId, `/trips/${projectId}`)).then((r) => r.data);
export const getClubTripByTrip = (clubId, tripId) =>
  authAxios.get(clubPath(clubId, `/trips/by-trip/${tripId}`)).then((r) => r.data);
export const updateClubTrip = (clubId, projectId, payload) =>
  authAxios.put(clubPath(clubId, `/trips/${projectId}`), payload).then((r) => r.data);
export const updateClubTripDetails = (clubId, projectId, payload) =>
  authAxios.put(clubPath(clubId, `/trips/${projectId}/details`), payload).then((r) => r.data);
export const regenerateClubTripDetails = (clubId, projectId) =>
  authAxios.post(clubPath(clubId, `/trips/${projectId}/details/regenerate`), {}, { timeout: 120000 }).then((r) => r.data);
export const confirmClubTrip = (clubId, projectId) =>
  authAxios.post(clubPath(clubId, `/trips/${projectId}/confirm`)).then((r) => r.data);

// Links a club shares carry ?ref=<handle>: a booking through them is the club's own
// guest (no Sotravel discovery fee), and the visit counts as a club referral touch.
export const clubShareUrl = (path, handle) => {
  const url = new URL(path, window.location.origin);
  if (handle) url.searchParams.set("ref", handle);
  return url.toString();
};

// A visit through a ?ref=<handle> link (any page). The BE ignores refs that aren't
// a club. Logged-out visits carry the browser's client id and are claimed after
// login (claimClubVisits), like club page visits.
// Signed in: the token names the visitor (the server ignores a bare user id here).
// Signed out: the browser's client id, claimed by the account after login.
export const trackClubRef = (ref, clientId) =>
  (localStorage.getItem("JWT_TOKEN")
    ? authAxios.post("/v1/club/ref", { ref, client_id: clientId }, { skipAuthRedirect: true })
    : unauthAxios.post("/v1/club/ref", { ref, client_id: clientId })
  )
    .then((r) => r.data)
    .catch(() => null);

// Signed-in trip page views — once per browser session per trip. Never logs out
// on a stale token (background call).
export const trackTripPageView = (projectId, tripId) => {
  if (!projectId || !localStorage.getItem("JWT_TOKEN")) return Promise.resolve(null);
  const key = `trip_page_view_${projectId}`;
  try {
    if (sessionStorage.getItem(key)) return Promise.resolve(null);
    sessionStorage.setItem(key, "1");
  } catch (e) {
    /* storage unavailable: still record once */
  }
  return authAxios
    .post("/v1/club/trip-views", { project_id: projectId, trip_id: tripId || null }, { skipAuthRedirect: true })
    .catch(() => null);
};

// Extracts a readable message from a club API error.
export const clubErrorMessage = (error, fallback = "Something went wrong. Please try again.") =>
  error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;

export const cancelClubWithdrawalBooking = (clubId, withdrawalId, bookingKey) =>
  authAxios.post(clubPath(clubId, `/withdrawals/${withdrawalId}/cancel-booking`), { booking_key: bookingKey }).then(r => r.data);
